import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, CalendarDays, CheckCircle2, Clock3, Gift, Heart, Landmark, MessageCircle, Send, Sparkles } from "lucide-react";
import { parseISO } from "date-fns";
import { getSentEventWishesApi } from "../../app/api";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
const SECONDS_PER_DAY = 24 * 60 * 60;
const parseEventDate = (date) => typeof date === "string" ? parseISO(date) : new Date(date);

export default function UpcomingEventsModal({
  isOpen,
  onClose,
  birthdays = [],
  anniversaries = [],
  holidays = [],
  onLike,
  onComment,
  onWish,
  currentUser,
}) {
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [wishedEventIds, setWishedEventIds] = useState(new Set());
  const [sendingWishId, setSendingWishId] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!isOpen) return undefined;

    setNow(new Date());
    const intervalId = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [isOpen]);

  const formatDateWithDay = (date) =>
    parseEventDate(date).toLocaleDateString("en-US", {
      weekday: "long",
      day: "numeric",
      month: "short",
    });

  const getRecipientId = useCallback((event) =>
    event.recipient_id ||
    event.user_id ||
    event.employee_id ||
    event.meta?.user_id ||
    event.meta?.employee_id, []);

  const getIndiaDateKey = useCallback((value) => {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return "";

    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);

    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    return `${year}-${month}-${day}`;
  }, []);

  const getEventDisplayName = useCallback((event) => {
    const firstUser = event.meta?.users?.[0] || {};
    return (
      event.displayName ||
      event.meta?.display_name ||
      [event.meta?.first_name || firstUser.first_name, event.meta?.last_name || firstUser.last_name]
        .filter(Boolean)
        .join(" ") ||
      event.title ||
      event.name ||
      "Team member"
    );
  }, []);

  const getDedupeKey = useCallback((event) => {
    if (event.eventType === "festival" || event.eventType === "office_holiday") {
      return `${event.eventType}:${event.id || event.title || event.name}:${getIndiaDateKey(event.event_date)}`;
    }

    const recipientId = getRecipientId(event);
    const displayName = getEventDisplayName(event).trim().toLowerCase();
    const personKey = displayName || recipientId || event.id;

    return `${event.eventType}:${getIndiaDateKey(event.event_date)}:${personKey}`;
  }, [getEventDisplayName, getIndiaDateKey, getRecipientId]);

  const allEvents = useMemo(() => {
    const flattenedEvents = [
      ...birthdays.map(b => ({ ...b, eventType: 'birthday' })),
      ...anniversaries.map(a => ({
        ...a,
        eventType: a.eventType || (a.type === "new_joiner" ? "new_joiner" : "anniversary"),
      })),
      ...holidays.map(h => ({ ...h, eventType: h.eventType || h.type || "festival" })),
    ].flatMap((event) => {
      const isBirthday = event.eventType === 'birthday';
      const isNewJoiner = event.eventType === 'new_joiner';
      const isHoliday = event.eventType === "festival" || event.eventType === "office_holiday";
      
      if (!isBirthday && !isNewJoiner && !isHoliday && event.meta?.users) {
        return event.meta.users.map(u => ({
          ...event,
          ...u,
          id: `${event.id}-${u.id || u.user_id || u.employee_id || `${u.first_name || ""}-${u.last_name || ""}`}`,
          _originalId: event.id,
          _feedItem: event,
          displayName: `${u.first_name || ""} ${u.last_name || ""}`.trim() || event.meta?.display_name || "Team member",
          years: u.years_completed
        }));
      }
      
      return [{
        ...event,
        _originalId: event.id,
        _feedItem: event,
        displayName: getEventDisplayName(event),
        years: event.meta?.years_completed
      }];
    });

    const byPerson = new Map();

    flattenedEvents.forEach((event) => {
      const key = getDedupeKey(event);
      const existing = byPerson.get(key);

      if (!existing || new Date(event.event_date) < new Date(existing.event_date)) {
        byPerson.set(key, event);
      }
    });

    return [...byPerson.values()].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
  }, [birthdays, anniversaries, getDedupeKey, getEventDisplayName, holidays]);

  const handleSendComment = (id) => {
    if (!commentText.trim()) return;
    onComment(id, commentText);
    setCommentText("");
    setActiveCommentId(null);
  };

  const getWishKey = (event) =>
    `${event.eventType}:${getRecipientId(event)}:${getIndiaDateKey(event.event_date)}`;

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    getSentEventWishesApi().then((res) => {
      if (!mounted || !res.success) return;

      setWishedEventIds(
        new Set(
          (res.data || []).map((wish) => {
            const eventType = wish.event_type || (wish.type === "NEW_JOINER_WISH" ? "new_joiner" : "birthday");
            return `${eventType}:${wish.recipient_id}:${wish.event_date_key}`;
          })
        )
      );
    });

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleWish = async (event) => {
    const wishKey = getWishKey(event);
    if (!onWish || wishedEventIds.has(wishKey)) return;

    setSendingWishId(wishKey);
    const res = await onWish(event);
    setSendingWishId(null);

    if (res?.success) {
      setWishedEventIds((prev) => {
        const next = new Set(prev);
        next.add(wishKey);
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#05010f]/76 px-4 backdrop-blur-lg">

      <div className="relative w-full max-w-2xl overflow-hidden rounded-[26px] border border-white/12 bg-[#110a22] text-white shadow-[0_34px_120px_rgba(0,0,0,0.72),inset_0_1px_0_rgba(255,255,255,0.10)]">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),transparent_34%,rgba(168,85,247,0.10))]" />

        <div className="relative flex items-center justify-between border-b border-white/10 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-2xl border border-cyan-100/18 bg-cyan-300/12 text-cyan-100 shadow-[0_18px_40px_rgba(34,211,238,0.14)]">
              <CalendarDays className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.28em] text-cyan-100/52">
                Team Calendar
              </p>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-white">
                Today & Upcoming Events
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/8 text-white/70 transition hover:bg-white/14 hover:text-white"
            aria-label="Close events modal"
          >
            ✕
          </button>
        </div>

        <div className="relative max-h-[64vh] space-y-4 overflow-y-auto p-6 pr-5 no-scrollbar">

          {allEvents.map((event) => {
            const isBirthday = event.eventType === 'birthday';
            const isNewJoiner = event.eventType === 'new_joiner';
            const isOfficeHoliday = event.eventType === "office_holiday";
            const isHoliday = isOfficeHoliday || event.eventType === "festival";
            const isReadonly = event.readonly || event._feedItem?.readonly;
            const eventDate = parseEventDate(event.event_date);
            const isToday =
              now.getFullYear() === eventDate.getFullYear() &&
              now.getDate() === eventDate.getDate() &&
              now.getMonth() === eventDate.getMonth();
            // Calendar events begin at local midnight, regardless of a feed timestamp's time.
            const eventStart = new Date(eventDate).setHours(0, 0, 0, 0);
            const remainingSeconds = Math.ceil((eventStart - now.getTime()) / 1000);
            const showCountdown = !isToday && remainingSeconds > 0 && remainingSeconds < 7 * SECONDS_PER_DAY;
            const daysLeft = Math.floor(remainingSeconds / SECONDS_PER_DAY);
            const countdownTime = [
              Math.floor((remainingSeconds % SECONDS_PER_DAY) / 3600),
              Math.floor((remainingSeconds % 3600) / 60),
              remainingSeconds % 60,
            ].map((value, index) => `${String(value).padStart(2, "0")}${["h", "m", "s"][index]}`).join(" ");
            const recipientId = getRecipientId(event);
            const canWish =
              isToday &&
              recipientId &&
              String(recipientId) !== String(currentUser?.id);
            const wishKey = getWishKey(event);
            const alreadyWished = wishedEventIds.has(wishKey);
            const theme = isHoliday
              ? isOfficeHoliday
                ? {
                    card: "bg-[#17102a] border-emerald-200/20",
                    icon: "bg-emerald-300/14 text-emerald-100 ring-emerald-200/32",
                    badge: "bg-emerald-300/14 text-emerald-100 border-emerald-200/20",
                  }
                : {
                    card: "bg-[#17102a] border-amber-200/22",
                    icon: "bg-amber-300/14 text-amber-100 ring-amber-200/36",
                    badge: "bg-amber-300/14 text-amber-100 border-amber-200/20",
                  }
              : isBirthday
                ? {
                    card: "bg-[#17102a] border-pink-200/20",
                    icon: "ring-pink-200/70",
                    badge: "bg-pink-300/14 text-pink-100 border-pink-200/20",
                  }
                : isNewJoiner
                  ? {
                      card: "bg-[#17102a] border-cyan-200/20",
                      icon: "ring-cyan-200/70",
                      badge: "bg-cyan-300/14 text-cyan-100 border-cyan-200/20",
                    }
                  : {
                      card: "bg-[#17102a] border-violet-200/20",
                      icon: "ring-violet-200/70",
                      badge: "bg-violet-300/14 text-violet-100 border-violet-200/20",
                    };
            const typeLabel = isHoliday
              ? isOfficeHoliday ? "Office Holiday" : "Festival"
              : isBirthday ? "Birthday"
              : isNewJoiner ? "New Joiner"
              : "Anniversary";

            let subtext = "";
            if (isHoliday) {
              subtext = `${isOfficeHoliday ? "Office Holiday" : "Festival"} • ${isToday ? "Today" : formatDateWithDay(event.event_date)}`;
              if (event.meta?.description) {
                subtext = `${subtext} • ${event.meta.description}`;
              }
            } else if (isBirthday) {
              const age = new Date().getFullYear() - new Date(event.dob || event.event_date).getFullYear();
              subtext = `Turning ${age} • ${isToday ? "Today" : formatDateWithDay(event.event_date)}`;
            } else if (isNewJoiner) {
              subtext = `Joined company • ${isToday ? "Today" : formatDateWithDay(event.event_date)}`;
            } else {
              const years = event.years || 1;
              if (isToday) {
                subtext = `Completed ${years} Year${years > 1 ? 's' : ''} • Today`;
              } else {
                subtext = `Completing ${years} Year${years > 1 ? 's' : ''} • ${formatDateWithDay(event.event_date)}`;
              }
            }

            return (
              <div key={event.id} className="space-y-2">
                <div
                  className={`group relative flex items-center gap-4 overflow-hidden rounded-2xl border p-4 shadow-[0_18px_46px_rgba(0,0,0,0.24),inset_0_1px_0_rgba(255,255,255,0.08)] transition hover:-translate-y-0.5 hover:bg-[#1b1230] hover:shadow-[0_24px_60px_rgba(0,0,0,0.30)] ${theme.card}`}
                >
                  <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent" />

                  <div className="relative mr-1 shrink-0">
                    {isHoliday ? (
                      <div
                        className={`grid h-16 w-16 place-items-center rounded-3xl ring-2 shadow-[0_18px_36px_rgba(0,0,0,0.20)] ${theme.icon}`}
                      >
                        {isOfficeHoliday ? <Building2 size={28} /> : <Landmark size={28} />}
                      </div>
                    ) : (
                      <>
                        <img
                          src={`${IMAGE_BASE_URL}${event.profile_pic || event.meta?.profile_pic || event.publisher_profile_pic || '/default-avatar.png'}`}
                          alt={event.displayName}
                          className={`h-16 w-16 rounded-3xl object-cover ring-2 shadow-[0_18px_36px_rgba(0,0,0,0.24)] ${theme.icon}`}
                        />
                        <div className="absolute -bottom-1.5 -right-1.5 grid h-7 w-7 place-items-center rounded-2xl border border-white/20 bg-[#24183b]/90 text-white shadow-lg backdrop-blur-md">
                          {isBirthday ? <Gift size={14} /> : <Sparkles size={14} />}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${theme.badge}`}>
                        {typeLabel}
                      </span>
                      {isToday && (
                        <span className="rounded-full border border-cyan-200/24 bg-cyan-300/14 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-cyan-100">
                          Today
                        </span>
                      )}
                    </div>
                    <p className="truncate text-lg font-black tracking-tight text-white">
                      {event.displayName}
                    </p>

                    <p className="mt-1 text-sm font-medium text-white/62">
                      {subtext}
                    </p>
                    {showCountdown && (
                      <div
                        role="timer"
                        aria-label={`Time until ${event.displayName} (${typeLabel})`}
                        className={`mt-3 inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border px-3 py-2 text-xs ${theme.badge}`}
                      >
                        <Clock3 size={14} className="shrink-0" aria-hidden="true" />
                        <span className="font-bold">
                          {daysLeft > 0 ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "Less than 1 day left"}
                        </span>
                        <span className="font-mono font-semibold tabular-nums">
                          {countdownTime}
                        </span>
                      </div>
                    )}
                  </div>

                  {canWish && (
                    <button
                      type="button"
                      onClick={() => handleWish(event)}
                      disabled={alreadyWished || sendingWishId === wishKey}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-black transition ${
                        alreadyWished
                          ? "bg-emerald-400/15 text-emerald-200"
                          : "bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white shadow-lg shadow-fuchsia-900/30 hover:scale-[1.03]"
                      } disabled:cursor-not-allowed disabled:opacity-70`}
                    >
                      {alreadyWished ? (
                        <>
                          <CheckCircle2 size={14} />
                          Sent
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} />
                          {sendingWishId === wishKey ? "Sending..." : isBirthday ? "Wish" : "Congrats"}
                        </>
                      )}
                    </button>
                  )}

                  {!isReadonly && (
                    <div className="ml-2 flex shrink-0 items-center gap-2">
                      <button 
                        onClick={() => onLike(event._feedItem)}
                        className={`grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/8 transition ${event._feedItem.is_liked ? 'text-rose-300' : 'text-white/48 hover:bg-white/12 hover:text-rose-200'}`}
                      >
                        <Heart size={16} fill={event._feedItem.is_liked ? "currentColor" : "none"} />
                        <span className="text-[9px] font-bold leading-none">{event._feedItem.total_likes || 0}</span>
                      </button>

                      <button 
                        onClick={() => {
                          setActiveCommentId(activeCommentId === event.id ? null : event.id);
                          setCommentText("");
                        }}
                        className={`grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/8 transition ${activeCommentId === event.id ? 'text-cyan-200' : 'text-white/48 hover:bg-white/12 hover:text-cyan-200'}`}
                      >
                        <MessageCircle size={16} />
                        <span className="text-[9px] font-bold leading-none">{event._feedItem.comments?.length || 0}</span>
                      </button>
                    </div>
                  )}
                </div>

                {activeCommentId === event.id && !isReadonly && (
                  <div className="flex gap-2 px-2 animate-in slide-in-from-top-2 duration-200">
                    <input
                      autoFocus
                      type="text"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSendComment(event._feedItem.id)}
                      placeholder="Say something nice..."
                      className="flex-1 rounded-2xl border border-white/14 bg-white/9 px-4 py-2 text-sm text-white placeholder-white/38 outline-none transition focus:border-cyan-200/50 focus:ring-2 focus:ring-cyan-300/18"
                    />
                    <button
                      onClick={() => handleSendComment(event._feedItem.id)}
                      disabled={!commentText.trim()}
                      className="grid h-10 w-10 place-items-center rounded-2xl bg-cyan-500 text-white transition hover:bg-cyan-400 disabled:opacity-50"
                    >
                      <Send size={14} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {allEvents.length === 0 && (
            <div className="rounded-3xl border border-white/12 bg-white/7 px-6 py-10 text-center">
              <CalendarDays className="mx-auto h-10 w-10 text-cyan-100/62" />
              <p className="mt-3 text-sm font-bold text-white/78">No upcoming events</p>
              <p className="mt-1 text-xs text-white/42">Birthdays, anniversaries and holidays will appear here.</p>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
